/**
 * The device page's tab strip.
 *
 * Deliberately NOT Webwin's `TabControl`, for the same two reasons the XAML hand-rolled its own
 * rather than using OpenSilver's:
 *
 *   * **Predictable styling.** The strip is a row of underlined labels on the page background, not a
 *     framed notebook, and `TabControl` paints a themed frame around its page area.
 *   * **A HIDDEN PANEL STAYS ALIVE.** Every tab here has hub subscriptions - live metrics, install
 *     progress, history pushes - and a `TabControl` that disposes or never builds the page a tab is
 *     not showing would silently drop them. Here each tab is a panel whose `visible` is toggled, so
 *     an event landing on the Performance tab while History is showing still updates it.
 *
 * It also carries the attention dot the Software tab needs when a reboot or updates are pending, and
 * it scrolls sideways: six tabs are about 600px of labels, which does not fit a phone.
 */
import {
  Control,
  insets,
  type Color,
  type Container,
  type ControlOptions,
  type RenderContext,
  type ScrollViewer,
  type Size,
  type StackPanel,
  type Theme
} from 'webwin';
import { crm } from '../theme/CrmTheme.js';
import { CrmScroller } from './Scroller.js';
import { FONT, GAP, hstack } from './layout.js';

/** The gap either side of a tab's label, and the underline's height. */
const TAB_PADDING = 14;
const UNDERLINE = 3;

interface TabButtonOptions extends ControlOptions {
  text: string;
  onClick: () => void;
}

/**
 * One tab: a label with an underline under the selected one.
 *
 * **Not the rail's `NavButton`**, which was the first thing tried and is wrong twice over. It
 * measures to `available.width`, which is right against a fixed-width rail and produces a
 * sixty-million-pixel button inside a horizontally scrolling viewport, where the available width is
 * unbounded. And it paints for a dark band: a pale-grey label and a LEFT accent bar, where a tab
 * strip on a page wants body ink and an underline.
 */
class TabButton extends Control {
  #text: string;
  selected = false;
  attention = false;

  constructor(options: TabButtonOptions) {
    super({ height: 34, cursor: 'pointer', tabStop: true, ...options });
    this.#text = options.text;
    this.on('click', () => options.onClick());
    this.on('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.handled = true;
      options.onClick();
    });
  }

  get text(): string {
    return this.#text;
  }

  /** Measures to its own label, which is what lets the strip scroll rather than stretch. */
  override preferredSize(_available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    // Measured in the SELECTED font, so a tab does not change width as it is selected - a strip
    // whose tabs shuffle sideways under the pointer is the kind of thing nobody reports and
    // everybody notices.
    const m = desktop.rc.measureText(this.#text, FONT.bold);
    return { width: Math.ceil(m.width) + TAB_PADDING * 2, height: this.height };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const c = crm(theme);
    const r = this.localRect;

    const ink: Color = this.selected ? c.brand : this.hovered ? c.text : c.subtleText;
    const font = this.selected ? FONT.bold : FONT.body;
    // Centred by measuring: `drawText` takes a left edge, and the label is measured in the SELECTED
    // font above, so the unselected one has to be centred against its own width or it would sit off
    // to the left of the underline that appears under it.
    const width = rc.measureText(this.#text, font).width;
    rc.drawText(
      this.#text,
      r.x + (r.width - width) / 2,
      r.y + (r.height - UNDERLINE) / 2,
      font,
      ink,
      { baseline: 'middle' }
    );

    // The amber dot: something on that tab wants eyes - a pending reboot, updates waiting - and the
    // point of it is that it is visible from a tab nobody is looking at.
    if (this.attention) {
      rc.drawRect(
        { x: r.x + r.width - TAB_PADDING + 1, y: r.y + 6, width: 7, height: 7 },
        { fill: c.warn, radius: 3.5 }
      );
    }

    if (this.selected) {
      rc.fillRect(
        { x: r.x + 2, y: r.y + r.height - UNDERLINE, width: Math.max(0, r.width - 4), height: UNDERLINE },
        c.brand,
        1.5
      );
    }
  }
}

export interface TabSpec {
  /** Stable key. Used by `select` and worth keeping equal to the OpenSilver `SelectedTab` string. */
  readonly key: string;
  readonly label: string;
  /** The panel to show. Built once, kept alive, `visible`-toggled. */
  readonly panel: Control;
  /** Hidden entirely - the SNMP tab exists only while a monitor matches this machine. */
  visible?: boolean;
}

export class TabStrip {
  /** The strip. Add it above `host`. */
  readonly view: ScrollViewer;
  /** Where the selected tab's panel lives. Every tab's panel is a child of this. */
  readonly host: Container;

  readonly #buttons = new Map<string, TabButton>();
  readonly #tabs = new Map<string, TabSpec>();
  readonly #row: StackPanel;
  readonly #attention = new Set<string>();
  #selected = '';
  #onChange: ((key: string) => void) | null = null;

  constructor(host: Container, tabs: readonly TabSpec[]) {
    this.host = host;

    this.#row = hstack({ spacing: GAP.xs, padding: insets(0, 0, 0, GAP.sm) });
    // `dock: 'fill'` and never an auto height: a ScrollViewer does not measure itself, so an auto
    // height would leave the strip at its 160px constructor default with a 30px row inside it.
    this.view = new CrmScroller({
      height: 44,
      hAlign: 'stretch',
      verticalScroll: 'never',
      horizontalScroll: 'auto',
      showBorder: false,
      zoomEnabled: false
    });
    this.view.content.controls.add(this.#row);

    for (const tab of tabs) this.add(tab);
    const first = tabs.find(t => t.visible !== false);
    if (first) this.select(first.key);
  }

  add(tab: TabSpec): void {
    this.#tabs.set(tab.key, tab);

    const button = new TabButton({
      text: tab.label,
      autoWidth: true,
      visible: tab.visible !== false,
      onClick: () => this.select(tab.key)
    });
    this.#buttons.set(tab.key, button);
    this.#row.controls.add(button);

    tab.panel.visible = false;
    this.host.controls.add(tab.panel);
  }

  /** Shows or hides a whole tab - the SNMP tab appears only once a monitor is found. */
  setTabVisible(key: string, visible: boolean): void {
    const button = this.#buttons.get(key);
    const tab = this.#tabs.get(key);
    if (!button || !tab) return;
    button.visible = visible;
    if (!visible && this.#selected === key) {
      const fallback = [...this.#tabs.values()].find(
        t => t.key !== key && this.#buttons.get(t.key)?.visible
      );
      if (fallback) this.select(fallback.key);
    }
    this.#row.layout();
  }

  /**
   * The amber dot on a tab's label.
   *
   * The Software tab carries one when a reboot or updates are pending: the fact is on a tab nobody
   * is looking at, and a count nobody sees is a count that did not need rendering.
   */
  setAttention(key: string, on: boolean): void {
    if (this.#attention.has(key) === on) return;
    const button = this.#buttons.get(key);
    if (!button) return;
    button.attention = on;
    button.invalidate();
    if (on) this.#attention.add(key);
    else this.#attention.delete(key);
  }

  /** Whether a tab is currently flagged. Read by a caller that recomputes several at once. */
  hasAttention(key: string): boolean {
    return this.#attention.has(key);
  }

  get selected(): string {
    return this.#selected;
  }

  select(key: string): void {
    const tab = this.#tabs.get(key);
    if (!tab || this.#selected === key) return;
    this.#selected = key;

    for (const [k, spec] of this.#tabs) {
      spec.panel.visible = k === key;
      const button = this.#buttons.get(k);
      if (button) {
        button.selected = k === key;
        button.invalidate();
      }
    }
    this.host.layout();
    this.#reveal(key);
    this.#onChange?.(key);
  }

  /**
   * Scrolls the strip so the selected tab is on screen. On a phone the strip is wider than the
   * viewport, and a page opened on its fifth tab (a deep link, a remembered tab) would otherwise show
   * no selected tab at all. Deferred a frame, because the first selection happens before the strip
   * has been laid out and has a width to scroll within.
   */
  #reveal(key: string): void {
    requestAnimationFrame(() => {
      const button = this.#buttons.get(key);
      if (!button || this.#selected !== key) return;
      const viewport = this.view.width;
      const left = button.x;
      const right = left + button.width;
      const offset = this.view.scrollOffsetX;
      if (left < offset) this.view.scrollOffsetX = Math.max(0, left - GAP.lg);
      else if (right > offset + viewport) this.view.scrollOffsetX = right - viewport + GAP.lg;
    });
  }

  /** Notified after a tab change, for the first-open loads (the event log queries on first show). */
  onChange(fn: (key: string) => void): void {
    this.#onChange = fn;
  }
}
